import supabase from '../supabaseClient'

const RETURN_KEY = 'statbox.hub.return'
const PENDING_KEY = 'statbox.hub.pending'
const RETURN_TTL_MS = 60 * 60 * 1000
const PLATFORM_TIMEOUT_MS = 2500

export const HUB_HOME = '/hub'
export const HUB_BUY_CREDITS = '/hub/buy-credits'
const CREDITS_RETURN_KEY = 'statbox:hub_credits_return'

/** Remembers that a credit purchase started in the hub, so payment success returns there. */
export function markHubCreditsReturn() {
  try {
    sessionStorage.setItem(CREDITS_RETURN_KEY, '1')
  } catch {
    /* storage unavailable */
  }
}

/** True if the last credit purchase started in the hub. */
export function readHubCreditsReturn() {
  try {
    return sessionStorage.getItem(CREDITS_RETURN_KEY) === '1'
  } catch {
    return false
  }
}

export function clearHubCreditsReturn() {
  try {
    sessionStorage.removeItem(CREDITS_RETURN_KEY)
  } catch {
    /* storage unavailable */
  }
}

export function hubGamePath(matchId) {
  return `/hub/game/${matchId}`
}

export function publicGamePath(matchId, inviteToken) {
  const base = `/g/${matchId}`
  return inviteToken ? `${base}?invite=${encodeURIComponent(inviteToken)}` : base
}

export function publicGameUrl(matchId) {
  return `${window.location.origin}${publicGamePath(matchId)}`
}

function rpcError(error, fallback) {
  const message = String(error?.message || '').trim()
  if (/schema cache|could not find the function/i.test(message)) {
    return new Error('This page is not available yet. Try again shortly.')
  }
  return new Error(message || fallback)
}

export async function getHubGame(matchId, inviteToken = null) {
  const { data, error } = await supabase.rpc('hub_get_game', {
    p_match_id: matchId,
    p_invite_token: inviteToken || null,
  })
  if (error) throw rpcError(error, 'Could not open this game.')
  return data
}

export async function listHubGames() {
  const { data, error } = await supabase.rpc('hub_list_games')
  if (error) throw rpcError(error, 'Could not load your games.')
  return data
}

export async function claimSlot(matchId, tempStatId, userId = null) {
  const { error } = await supabase.rpc('hub_claim_slot', {
    p_match_id: matchId,
    p_temp_stat_id: tempStatId,
    p_user_id: userId,
  })
  if (error) throw rpcError(error, 'Could not save that.')
}

export async function unclaimSlot(matchId, tempStatId) {
  const { error } = await supabase.rpc('hub_unclaim_slot', {
    p_match_id: matchId,
    p_temp_stat_id: tempStatId,
  })
  if (error) throw rpcError(error, 'Could not remove that player.')
}

export async function claimChild(matchId, tempStatId, inviteToken = null) {
  const { data, error } = await supabase.rpc('hub_claim_child', {
    p_match_id: matchId,
    p_temp_stat_id: tempStatId,
    p_invite_token: inviteToken || null,
  })
  if (error) throw rpcError(error, 'Could not send that request.')
  return data
}

export async function reviewParent(requestId, approve) {
  const { error } = await supabase.rpc('hub_review_parent', {
    p_request_id: requestId,
    p_approve: approve,
  })
  if (error) throw rpcError(error, 'Could not save that decision.')
}

/** Emails the team admin that a player identified themselves (or a parent confirmed their child). Never throws. */
export function notifyIdentified({ matchId, tempStatId, kind = 'self', inviteToken = null }) {
  if (!matchId || !tempStatId) return
  supabase.functions
    .invoke('hub-identified-notify', {
      body: { match_id: matchId, temp_stat_id: tempStatId, kind, invite_token: inviteToken, site: window.location.origin },
    })
    .catch(() => {})
}

/** Parents confirm their child from the invite link, no account needed. */
export async function acceptParentInvite(token, childName) {
  const { data, error } = await supabase.rpc('hub_accept_parent_invite', {
    p_token: token,
    p_child_name: childName,
  })
  if (error) throw rpcError(error, 'Could not save that. Try again.')
  return data
}

export async function acceptInvite(token) {
  const { data, error } = await supabase.rpc('hub_accept_invite', { p_token: token })
  if (error) throw rpcError(error, 'This invite link is no longer valid.')
  return data
}

/** Joins from a shared game link (no personal invite). We email them when the stats are ready. */
export async function joinGameByLink(matchId) {
  const { data, error } = await supabase.rpc('hub_join_game', { p_match_id: matchId })
  if (error) throw rpcError(error, 'Could not join this game. Try again.')
  return data
}

export async function hasJoinedGame(matchId) {
  const { data, error } = await supabase.rpc('hub_viewer_joined', { p_match_id: matchId })
  if (error) return false
  return Boolean(data)
}

/** Creates invites and emails them. Returns one result per address. */
export async function sendInvites(matchId, invites) {
  const { data, error } = await supabase.functions.invoke('hub-game-invite', {
    body: { match_id: matchId, invites, site: window.location.origin },
  })
  if (error) {
    let message = error.message
    try {
      const body = await error.context?.json?.()
      if (body?.error) message = body.error
    } catch {
      /* keep the generic message */
    }
    throw new Error(message || 'Could not send the invites.')
  }
  return data
}

/**
 * Where sign-in should land after /signup/callback. Stored in localStorage
 * because email sign-in links usually open in a new tab.
 */
export function rememberHubReturn(path) {
  try {
    localStorage.setItem(RETURN_KEY, JSON.stringify({ path, at: Date.now() }))
  } catch {
    /* storage unavailable */
  }
}

export function clearHubReturn() {
  try {
    localStorage.removeItem(RETURN_KEY)
  } catch {
    /* storage unavailable */
  }
}

export function consumeHubReturn() {
  try {
    const raw = localStorage.getItem(RETURN_KEY)
    if (!raw) return ''
    localStorage.removeItem(RETURN_KEY)
    const { path, at } = JSON.parse(raw) || {}
    if (!path || Date.now() - Number(at || 0) > RETURN_TTL_MS) return ''
    return String(path).startsWith('/') && !String(path).startsWith('//') ? path : ''
  } catch {
    return ''
  }
}

/** An action the visitor chose before signing in, replayed once they return. */
export function savePendingAction(matchId, action) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ matchId, action, at: Date.now() }))
  } catch {
    /* storage unavailable */
  }
}

export function takePendingAction(matchId) {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    if (!raw) return null
    const saved = JSON.parse(raw) || {}
    if (saved.matchId !== matchId) return null
    localStorage.removeItem(PENDING_KEY)
    if (Date.now() - Number(saved.at || 0) > RETURN_TTL_MS) return null
    return saved.action || null
  } catch {
    return null
  }
}

export async function sendEmailSignInLink(email, returnPath) {
  rememberHubReturn(returnPath)
  const { error } = await supabase.auth.signInWithOtp({
    email: String(email || '').trim(),
    options: {
      emailRedirectTo: `${window.location.origin}/signup/callback`,
      shouldCreateUser: true,
    },
  })
  if (error) throw rpcError(error, 'Could not send the sign-in link.')
}

/** Reads a community's platform, giving up quickly so routing never stalls. */
export async function fetchCommunityPlatform(communityId) {
  if (!communityId) return null
  const lookup = supabase
    .from('communities')
    .select('platform, name, credits')
    .eq('id', communityId)
    .maybeSingle()
    .then(({ data, error }) => (error ? null : data))
    .catch(() => null)
  const timeout = new Promise((resolve) => {
    window.setTimeout(() => resolve(null), PLATFORM_TIMEOUT_MS)
  })
  return Promise.race([lookup, timeout])
}

export function isBasicCommunity(row) {
  return row?.platform === 'basic'
}
